/**
 * When a screen fails: a plain-language panel instead of a blank page, a
 * report to the server (POST /api/client-errors) so the failure shows up in
 * the server log, and one automatic reload when a page's code cannot be
 * loaded because the site was redeployed while this tab was open.
 */
import { Component, lazy, type ComponentType, type ErrorInfo, type ReactNode } from "react";

/** The entry script's file name (it changes with every deploy): tells a stale tab apart. */
function buildId() {
  try {
    const src = document.querySelector<HTMLScriptElement>("script[type=module][src]")?.src ?? "";
    return src.split("/").pop()?.split("?")[0] ?? "";
  } catch {
    return "";
  }
}

/** Sends what failed to the server; never throws and never retries. */
export function reportClientError(
  error: unknown,
  details: { page?: string; componentStack?: string | null; kind?: "render" | "chunk" } = {},
) {
  try {
    const e = error instanceof Error ? error : new Error(String(error));
    void fetch("/api/client-errors", {
      method: "POST",
      credentials: "include",
      keepalive: true,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        message: e.message.slice(0, 1000),
        stack: (e.stack ?? "").slice(0, 4000),
        componentStack: (details.componentStack ?? "").slice(0, 4000),
        page: details.page ?? "",
        kind: details.kind ?? (isChunkLoadError(e) ? "chunk" : "render"),
        url: location.pathname,
        build: buildId(),
      }),
    }).catch(() => undefined);
  } catch {
    /* reporting must never add a second failure */
  }
}

/** True for the browser errors of a page chunk that could not be fetched (Chrome, Safari, Firefox, Vite). */
export function isChunkLoadError(error: unknown) {
  const e = error as { name?: string; message?: string } | null;
  return (
    !!e &&
    (e.name === "ChunkLoadError" ||
      /dynamically imported module|Importing a module script failed|error loading dynamically imported module|Unable to preload CSS|Loading (CSS )?chunk [\w-]+ failed/i.test(
        e.message ?? "",
      ))
  );
}

const RELOAD_KEY = "parlyst-chunk-reload";
/** One automatic reload per minute at most, so a missing file can never cause a reload loop. */
const RELOAD_WINDOW_MS = 60_000;
export const chunkReload = {
  /** Replaced in tests. */
  reload: () => location.reload(),
  /** Reloads the page unless it already reloaded for this reason in the last minute; true when it did. */
  tryReload() {
    try {
      const last = Number(sessionStorage.getItem(RELOAD_KEY) || 0);
      if (Date.now() - last < RELOAD_WINDOW_MS) return false;
      sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
    } catch {
      return false; // no session storage: never risk a loop
    }
    chunkReload.reload();
    return true;
  },
};

/**
 * Loads a page's code. After a deploy the old file names are gone, so the
 * import fails in a tab opened before it: reload once to pick up the new
 * version (the page shows its loading state meanwhile). A second failure
 * within a minute reaches the error panel.
 */
export function loadOrReload<T>(load: () => Promise<T>): Promise<T> {
  return load().catch((error) => {
    if (isChunkLoadError(error) && chunkReload.tryReload()) return new Promise<T>(() => {});
    throw error;
  });
}
/** React.lazy with the reload-once behaviour of loadOrReload. */
export function lazyPage<T extends ComponentType<any>>(load: () => Promise<{ default: T }>) {
  return lazy(() => loadOrReload(load));
}
/** Vite reports failed preloads of a chunk's dependencies with this event. */
export function reloadOnPreloadError() {
  try {
    window.addEventListener("vite:preloadError", (event) => {
      if (chunkReload.tryReload()) event.preventDefault();
    });
  } catch {
    /* no window */
  }
}

type BoundaryProps = {
  children: ReactNode;
  /** Which screen this is (sent with the report). */
  page: string;
  /** Changing it (another page opened) clears the panel. */
  resetKey?: string;
  /** Opens Today in the app; without it the link loads the start page. */
  onToday?: () => void;
};
type BoundaryState = { error: Error | null; resetKey?: string };

/** Shows "Something went wrong on this screen" instead of unmounting the whole app. */
export class ScreenErrorBoundary extends Component<BoundaryProps, BoundaryState> {
  state: BoundaryState = { error: null, resetKey: this.props.resetKey };
  static getDerivedStateFromError(error: unknown): Partial<BoundaryState> {
    return { error: error instanceof Error ? error : new Error(String(error)) };
  }
  static getDerivedStateFromProps(props: BoundaryProps, state: BoundaryState): Partial<BoundaryState> | null {
    return props.resetKey !== state.resetKey ? { error: null, resetKey: props.resetKey } : null;
  }
  componentDidCatch(error: Error, info: ErrorInfo) {
    reportClientError(error, { page: this.props.page, componentStack: info.componentStack });
  }
  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    const chunk = isChunkLoadError(error);
    return (
      <section className="card screen-error" role="alert">
        <h2>Something went wrong on this screen</h2>
        <p>
          {chunk
            ? "Parlyst was updated while this page was open, and this screen could not load. Reload to get the new version."
            : "Parlyst could not show this screen. Your saved records are safe. Reload to try again, or go back to Today."}
        </p>
        <p className="hint">
          <code>{error.message}</code>
        </p>
        <div className="screen-error-actions">
          <button className="button" onClick={() => location.reload()}>
            Reload
          </button>
          <a
            className="text-button"
            href="/"
            onClick={(e) => {
              if (!this.props.onToday) return;
              e.preventDefault();
              this.setState({ error: null });
              this.props.onToday();
            }}
          >
            Back to Today
          </a>
        </div>
      </section>
    );
  }
}
